import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, Check, X, Calendar, Users, MapPin, Clock, ArrowRight, ShieldCheck, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api from "../lib/api";
import { fmtDate } from "../lib/date";
import Reveal from "../components/Reveal";
import { PillButton, Overline } from "../components/primitives";

export default function Retreat() {
  const [r, setR] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [active, setActive] = useState(0);
  const [selDep, setSelDep] = useState(null);

  useEffect(() => {
    api.get("/retreats/el-salvador-6day").then((res) => { setR(res.data); setSelDep(res.data.departures?.[0]?.id || null); }).catch(() => {});
    api.get("/hotels").then((res) => setHotel(res.data[0])).catch(() => {});
  }, []);

  if (!r) return <div className="min-h-screen grid place-items-center pt-20 text-primary font-display">Loading retreat…</div>;

  const dep = r.departures?.find((d) => d.id === selDep) || null;
  const db_ = dep || r;

  return (
    <div className="pt-16 md:pt-20">
      {/* Header */}
      <section className="relative h-[70vh] overflow-hidden">
        <img src={r.gallery?.[active] || r.hero_image} alt={r.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-6 md:px-10 pb-10 text-white">
          <div className="flex flex-wrap gap-3 mb-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold"><Plane className="w-3.5 h-3.5" /> Flights Included</span>
            <span className="rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold">Adults 18+ Only</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl max-w-3xl leading-[0.95]">{r.name}</h1>
        </div>
      </section>

      {/* Gallery thumbs */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 -mt-8 relative z-10">
        <div className="flex gap-3 overflow-x-auto pb-2">
          {r.gallery?.map((g, i) => (
            <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
              className={`shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 ${active === i ? "border-primary" : "border-white"} transition-colors`}>
              <img src={g} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-3 gap-12">
        {/* Main */}
        <div className="lg:col-span-2 space-y-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Calendar, label: "Dates", value: `${fmtDate(db_.start_date)}` },
              { icon: Clock, label: "Duration", value: `${r.duration_days || 6} Days` },
              { icon: Users, label: `Min ${r.min_travelers} · ${r.spots_remaining} left`, value: r.fitness_level || "Intermediate" },
              { icon: MapPin, label: "Location", value: "El Salvador" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl border border-black/10 bg-white p-5">
                <s.icon className="w-5 h-5 text-primary" />
                <div className="overline mt-3 text-foreground/50">{s.label}</div>
                <div className="font-display font-bold mt-1 text-sm">{s.value}</div>
              </div>
            ))}
          </div>

          <div>
            <Overline>Availability</Overline>
            <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl md:text-4xl" data-testid="choose-date">Choose Your Retreat Date</h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {r.departures?.map((d) => (
                <button key={d.id} onClick={() => setSelDep(d.id)} data-testid={`date-${d.id}`}
                  className={`text-left rounded-2xl border p-5 transition-colors ${selDep === d.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-black/10 bg-white hover:border-primary/40"}`}>
                  <div className="font-display font-bold">{fmtDate(d.start_date)} – {fmtDate(d.end_date)}</div>
                  <div className="text-sm text-foreground/70 mt-1">{d.label}</div>
                  <div className="text-xs text-primary mt-2">{d.spots_remaining} of {d.capacity} spots left · 6 days / 5 nights</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Overline>Overview</Overline>
            <p className="mt-4 text-lg text-foreground/75 leading-relaxed">{r.summary}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {r.highlights?.map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {h}</div>
              ))}
            </div>
          </div>

          {/* Itinerary */}
          <div>
            <Overline>Day by Day</Overline>
            <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl md:text-4xl">Six-day itinerary</h2>
            <div className="mt-8 space-y-4">
              {r.itinerary?.map((d) => (
                <Reveal key={d.day}>
                  <div className="flex gap-5 rounded-2xl border border-black/10 bg-white p-6">
                    <div className="shrink-0 w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center font-display font-extrabold text-lg">D{d.day}</div>
                    <div>
                      <div className="font-display font-bold text-lg">{d.title}</div>
                      <div className="text-sm text-primary flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {d.location}</div>
                      <ul className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm text-foreground/70">
                        {d.activities?.map((a, k) => <li key={k} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> {a}</li>)}
                      </ul>
                      {d.note && <div className="mt-2 text-xs text-primary italic">{d.note}</div>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Included / not included */}
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><Check className="w-5 h-5 text-primary" /> What's included</h3>
              <ul className="mt-4 space-y-2 text-sm text-foreground/75">
                {r.included?.map((i, k) => <li key={k} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {i}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><X className="w-5 h-5 text-foreground/40" /> What's not included</h3>
              <ul className="mt-4 space-y-2 text-sm text-foreground/75">
                {r.not_included?.map((i, k) => <li key={k} className="flex items-start gap-2"><X className="w-4 h-4 text-foreground/30 mt-0.5 shrink-0" /> {i}</li>)}
              </ul>
            </div>
          </div>

          {/* Trip details */}
          <div>
            <Overline>Trip Details</Overline>
            <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl">Everything you need to know</h2>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-black/10 bg-white p-6">
                <h3 className="font-display font-bold flex items-center gap-2"><Plane className="w-5 h-5 text-primary" /> Flights</h3>
                <p className="mt-2 text-sm text-foreground/70">Round-trip flights are included from <b>{r.flights?.departure_airport}</b> to {r.flights?.arrival}.</p>
                {r.flights?.disclosure && <p className="mt-2 text-xs text-primary italic">*{r.flights.disclosure}</p>}
              </div>
              <div className="rounded-2xl border border-black/10 bg-white p-6">
                <h3 className="font-display font-bold flex items-center gap-2"><Check className="w-5 h-5 text-primary" /> Meals</h3>
                <ul className="mt-2 space-y-1 text-sm text-foreground/70">
                  {r.meals?.map((m, i) => <li key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> {m}</li>)}
                </ul>
                {r.meals_note && <p className="mt-2 text-xs text-foreground/50 italic">{r.meals_note}</p>}
              </div>
              <div className="rounded-2xl border border-black/10 bg-white p-6">
                <h3 className="font-display font-bold flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Transportation</h3>
                <p className="mt-2 text-sm text-foreground/70">{r.transportation}</p>
              </div>
              <div className="rounded-2xl border border-black/10 bg-white p-6">
                <h3 className="font-display font-bold flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Minimum travelers</h3>
                <p className="mt-2 text-sm text-foreground/70">{r.min_travelers_policy}</p>
              </div>
            </div>
          </div>

          {/* Hotel */}
          {hotel && (
            <div>
              <Overline>Where You'll Stay</Overline>
              <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl">{hotel.name}</h2>
              <p className="mt-3 text-foreground/70">{hotel.description}</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {hotel.photos?.map((p, i) => <img key={i} src={p} alt="Hotel" className="rounded-2xl h-52 w-full object-cover" />)}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {hotel.amenities?.map((a, i) => <span key={i} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">{a}</span>)}
              </div>
              <div className="mt-4 rounded-xl bg-secondary/50 p-4 text-sm text-foreground/70">
                <b>Rooms:</b> {hotel.room_info}
                {hotel.upgrade_note && <div className="mt-1 text-xs text-primary italic">*{hotel.upgrade_note}{hotel.upgrade_price ? ` (${hotel.upgrade_price})` : ""}</div>}
              </div>
            </div>
          )}

          {/* FAQ */}
          {r.faqs?.length > 0 && (
            <div>
              <Overline>Good to Know</Overline>
              <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl">Retreat FAQs</h2>
              <Accordion type="single" collapsible className="mt-6">
                {r.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}>
                    <AccordionTrigger className="text-left font-display font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-foreground/70">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Policies */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-black/10 p-6 bg-white">
              <h3 className="font-display font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Cancellation Policy</h3>
              <p className="mt-3 text-sm text-foreground/70">{r.cancellation_policy}</p>
            </div>
            <div className="rounded-2xl border border-black/10 p-6 bg-white">
              <h3 className="font-display font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Travel Protection</h3>
              <p className="mt-3 text-sm text-foreground/70">{r.travel_protection}</p>
            </div>
          </div>
        </div>

        {/* Sticky booking panel */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 rounded-3xl border border-black/10 bg-white p-7 shadow-sm">
            <div className="flex items-baseline gap-2">
              <span className="font-display font-extrabold text-4xl">${r.price?.toLocaleString()}</span>
              <span className="text-foreground/50 text-sm">per person</span>
            </div>
            <div className="mt-1 text-sm text-primary font-medium">Flights included · All-inclusive</div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-foreground/60">Departure</span><span className="font-medium">{fmtDate(db_.start_date)}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Return</span><span className="font-medium">{fmtDate(db_.end_date)}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Deposit from</span><span className="font-medium">${r.deposit_amount?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Spots remaining</span><span className="font-medium">{db_.spots_remaining} of {db_.capacity}</span></div>
            </div>

            <div className="mt-6 h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${(db_.spots_booked / db_.capacity) * 100}%` }} />
            </div>
            <div className="mt-2 text-xs text-foreground/50">{db_.spots_booked} of {db_.capacity} spots booked</div>

            <Link to={`/book?departure=${selDep || ""}`}><PillButton data-testid="retreat-book-btn" className="mt-6 w-full h-13">Book Your Retreat <ArrowRight className="w-4 h-4" /></PillButton></Link>
            <p className="mt-3 text-center text-xs text-foreground/50">Deposit, full payment & installments available</p>
            <div className="mt-4 rounded-xl bg-secondary/60 p-3 text-xs text-foreground/60 text-center">Adults 18+ only · Age confirmed at checkout</div>
          </div>
        </div>
      </div>
    </div>
  );
}
